// Today.tsx — экран «Сегодня» (Ф4). KPI + список респондентов × анкета со
// статусом сдачи на сегодня (из /api/today). Клик по строке с run'ом → отчёт.
// AI-саммари — явная заглушка (Жетон 4 не реализован, R-AI-долг плана).

import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { TodayResponse } from '../api/contract';
import { StateCard } from '../components/StateBlock';
import { humanDate, hhmm, initials } from '../lib/format';
import { statusKey } from '../lib/status';
import { useApi } from '../lib/useApi';

type Survey = TodayResponse['respondents'][number]['surveys'][number];

/** Подпись под статусом строки: «сдан · 18:21», «заполняет · 9 ответов» и т.п. */
function statusDetail(s: Survey): string {
  const key = statusKey(s.status);
  switch (key) {
    case 'done':
      return s.completedAt ? `сдан · ${hhmm(s.completedAt)}` : 'сдан';
    case 'prog':
      return `заполняет · ${s.answerCount} отв.`;
    case 'aband':
      return 'не сдан';
    default:
      return s.status === 'pending' ? 'не начат' : 'ожидается';
  }
}

export default function Today() {
  const { data, loading, error } = useApi<TodayResponse>(() => api.today(), []);
  const navigate = useNavigate();

  return (
    <section>
      <div className="ph">
        <h1>Сегодня</h1>
        {data && (
          <span className="sub" style={{ margin: 0 }}>
            {humanDate(data.date)}
          </span>
        )}
      </div>
      <p className="sub">Кто сдал ежедневный отчёт, а кто нет.</p>

      {loading && <StateCard kind="loading" message="Загрузка…" />}
      {error && <StateCard kind="error" message={error} />}

      {data && (
        <>
          <div className="kpis">
            <div className="kpi">
              <div className="l">Отчётов сдано</div>
              <div className="v">
                {data.kpis.completed} / {data.kpis.assignmentsTotal}
              </div>
            </div>
            <div className="kpi">
              <div className="l">Заполняют сейчас</div>
              <div className="v" style={{ color: data.kpis.inProgress ? 'var(--yellow)' : undefined }}>
                {data.kpis.inProgress}
              </div>
            </div>
            <div className="kpi">
              <div className="l">Не начато</div>
              <div className="v">{data.kpis.pending + data.kpis.waiting}</div>
            </div>
            <div className="kpi">
              <div className="l">Не сдано</div>
              <div className="v" style={{ color: data.kpis.abandoned ? 'var(--red)' : undefined }}>
                {data.kpis.abandoned}
              </div>
            </div>
          </div>

          <div className="grid2">
            <div className="card">
              <h3>
                Статус отчётов <span className="tag">сегодня</span>
              </h3>
              {data.respondents.length === 0 && (
                <p style={{ color: 'var(--muted)', margin: 0 }}>На сегодня нет назначенных отчётов.</p>
              )}
              {data.respondents.flatMap((r) =>
                r.surveys.map((s) => {
                  const key = statusKey(s.status);
                  const clickable = s.runId !== null;
                  return (
                    <div
                      key={`${r.respondentId}:${s.surveyCode}`}
                      className="rrow"
                      style={clickable ? undefined : { cursor: 'default' }}
                      onClick={clickable ? () => navigate(`/report/${s.runId}`) : undefined}
                    >
                      <div className="avatar">{initials(r.name)}</div>
                      <div className="nm">
                        <b>{r.name}</b>
                        <small>
                          {r.role} · {s.surveyCode}
                        </small>
                      </div>
                      <span className={`st ${key}`}>
                        <span className={`dot ${key}`} />
                        {statusDetail(s)}
                      </span>
                    </div>
                  );
                }),
              )}
            </div>

            <div className="ai card">
              <h3>
                <span>🤖 AI-саммари дня</span> <span className="badge">появится после Ф4.S2</span>
              </h3>
              <p style={{ color: 'var(--muted)', margin: 0, fontSize: 12 }}>
                Авто-разбор сданных отчётов от Claude (опоздания, конфликты, долгострои) появится,
                когда будет реализован Жетон 4. Сейчас это заглушка — данных AI ещё нет.
              </p>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
